import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { format } from 'date-fns';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [listingCategories, setListingCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user data from Firebase
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        // Get user data
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) {
          throw new Error('User not found');
        }
        
        const userData = {
          id: userDoc.id,
          ...userDoc.data()
        };
        setUser(userData);
        
        // Get vehicles from vehicles collection
        try {
          const vehiclesQuery = query(
            collection(db, 'vehicles'),
            where('owner', '==', userId)
          );
          const vehiclesSnapshot = await getDocs(vehiclesQuery);
          const vehiclesData = vehiclesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setVehicles(vehiclesData);
        } catch (err) {
          console.log('No vehicles found or error loading vehicles:', err);
          setVehicles([]);
        }
        
        // Get active reservations
        try {
          const reservationsQuery = query(
            collection(db, 'reservations'),
            where('booker', '==', userId),
            where('state', '==', 'checkin')
          );
          const reservationsSnapshot = await getDocs(reservationsQuery);
          const reservationsData = reservationsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setReservations(reservationsData);
        } catch (err) {
          console.log('No reservations found or error loading reservations:', err);
          setReservations([]);
        }

        // Get listing categories for vehicle type mapping
        try {
          const categoriesSnapshot = await getDocs(collection(db, 'listingcategories'));
          const categoriesData = categoriesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setListingCategories(categoriesData);
        } catch (err) {
          console.log('Error loading listing categories:', err);
          setListingCategories([]);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to load user details');
        console.error('Error loading user data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      loadUserData();
    }
  }, [userId]);

  const handleRemoveUser = async () => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      try {
        // Add user deletion logic here
        alert('User removed successfully!');
        navigate('/parking-user-list');
      } catch (error) {
        console.error('Error removing user:', error);
        alert('Failed to remove user');
      }
    }
  };

  const handleAddNewSpace = () => {
    // Add new space logic will be implemented when connecting to backend
    console.log('Adding new space for user:', userId);
  };

  const handlePrev = () => {
    // Navigate to previous user
    console.log('Navigate to previous user');
  };

  const handleNext = () => {
    // Navigate to next user
    console.log('Navigate to next user');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDuration = (duration, durationUnit) => {
    if (!duration) return 'N/A';
    
    const unitMap = {
      'hourly': 'Hr',
      'daily': 'Day',
      'weekly': 'Week'
    };
    
    const unit = unitMap[durationUnit] || durationUnit;
    return `${duration} ${unit}`;
  };

  const getVehicleTypeDisplay = (vehicleType) => {
    if (!vehicleType) return 'N/A';
    
    // Find the category that matches the vehicle type index
    const category = listingCategories.find(cat => cat.index === vehicleType);
    return category ? category.ContextValue : vehicleType;
  };

  if (loading) {
    return (
      <div className="h-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading user details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="h-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'User not found'}</p>
            <button
              onClick={() => navigate('/parking-user-list')}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              ← Back to Users
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/parking-user-list')}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">User Detail</h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleAddNewSpace}
              className="btn-primary flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              ADD NEW SPACE
            </button>
            <button
              onClick={handlePrev}
              className="btn-primary flex items-center"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              PREV
            </button>
            <button
              onClick={handleNext}
              className="btn-primary flex items-center"
            >
              NEXT
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>

        {/* User Information Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start space-x-6">
            {/* Avatar */}
            {user.avatar ? (
              <img
                src={user.avatar}
                alt="User Avatar"
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-4xl text-gray-500">
                {user.firstname?.charAt(0) ?? 'U'}
              </div>
            )}
            
            {/* User Details */}
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600 mb-1">
                    <span className="font-medium">Membership From:</span> {formatDate(user.created)}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">First Name:</span> {user.firstname || 'N/A'}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">Last Name:</span> {user.lastname || 'N/A'}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">Phone Number:</span> {user.phone || 'N/A'}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">Email Address:</span> {user.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">Address Line 1:</span> {user.address1 || 'N/A'}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">Address Line 2:</span> {user.address2 || 'N/A'}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">Address Line 3:</span> {user.address3 || 'N/A'}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">City:</span> {user.city || 'N/A'}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">State:</span> {user.state || 'N/A'}
                  </p>
                  <p className="text-gray-900 mb-1">
                    <span className="font-medium">Zipcode:</span> {user.zipcode || 'N/A'}
                  </p>
                </div>
              </div>
              
              {/* Remove User Button */}
              <div className="mt-4">
                <button
                  onClick={handleRemoveUser}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  REMOVE USER
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Attendant Detail Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendant Detail</h3>
          <p className="text-gray-600">No Attendant Access</p>
        </div>

        {/* Registered Vehicles Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Registered Vehicles</h3>
          {vehicles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Model
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getVehicleTypeDisplay(vehicle.type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600">No registered vehicles found.</p>
          )}
        </div>

        {/* Active Reservations Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Reservations</h3>
          {reservations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle Number
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reservations.map((reservation) => (
                    <tr key={reservation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(reservation.reserveDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDuration(reservation.duration, reservation.durationunit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${reservation.total || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {reservation.vehicleData?.number || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600">No active reservations found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetail; 